import React from 'react';
import { Outlet, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import WorkIcon from '@mui/icons-material/Work';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import EuroIcon from '@mui/icons-material/Euro';
import LogoutIcon from '@mui/icons-material/Logout';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const DRAWER_WIDTH = 260;

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { t } = useLanguage();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems: NavItem[] = [
    { text: t('admin.dashboard'), icon: <DashboardIcon />, path: '/admin' },
    { text: t('admin.services'), icon: <BusinessCenterIcon />, path: '/admin/services' },
    { text: t('admin.kooperation'), icon: <WorkIcon />, path: '/admin/kooperation' },
    { text: t('admin.preise'), icon: <EuroIcon />, path: '/admin/preise' },
    { text: t('admin.contacts'), icon: <ContactMailIcon />, path: '/admin/contacts' },
    { text: 'Kunden', icon: <PeopleAltIcon />, path: '/admin/customers' },
    { text: 'Über uns', icon: <InfoOutlinedIcon />, path: '/admin/ueber-uns' },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          ml: `${DRAWER_WIDTH}px`,
          bgcolor: 'white',
          color: 'primary.main',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Admin Dashboard
          </Typography>
          <IconButton onClick={handleLogout} sx={{ color: 'primary.main' }}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Drawer
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: '#1a1a1a',
            color: '#e8e8e8',
            borderRight: 'none',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Toolbar sx={{ borderBottom: '1px solid rgba(255,255,255,0.07)', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', letterSpacing: '-0.3px' }}>
            TOP BHS
          </Typography>
        </Toolbar>
        <List sx={{ px: 1 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  selected={isActive}
                  sx={{
                    borderRadius: 1.5,
                    color: isActive ? '#ffffff' : 'rgba(255,255,255,0.6)',
                    bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.07)',
                      color: '#ffffff',
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.13)' },
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive ? 600 : 400 }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, mt: 8 }}
      >
        <Container maxWidth="xl">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default AdminLayout;
